import {
    AlertCircle,
    ArrowRight,
    ArrowUpRight,
    BarChart3,
    DollarSign,
    Flame,
    Globe2,
    Layers,
    MousePointerClick,
    Radio,
    Send,
    TrendingUp,
    type LucideIcon,
} from "lucide-react";
import {
    Area,
    CartesianGrid,
    Cell,
    ComposedChart,
    Line,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { useOverview } from "../api/queries";
import { useDashboardStore } from "../store/dashboardStore";
import { PageHeader } from "../components/PageHeader";
import { PageShell } from "../components/PageShell";
import { SectionCard } from "../components/SectionCard";
import { KpiCard } from "../components/KpiCard";
import { LegendDot } from "../components/BadgeHi";
import { DashSkeleton, SkelText } from "../components/Skeleton";
import { ApiBanner } from "../components/ApiBanner";
import {
    cur,
    deltaDir,
    fmt,
    fmtDelta,
    fmtPct,
} from "../lib/format";
import { correctFailureRate } from "../lib/numbers";
import { cn } from "@/lib/utils";

const MIX_COLORS: Record<string, string> = {
    flow: "#3b82f6",
    broadcast: "#f59e0b",
    esteira: "#8b5cf6",
};

export function HomeView() {
    const setView = useDashboardStore((s) => s.setView);
    const { data, error, isLoading, refetch } = useOverview();
    const loading = isLoading && !data;

    const hero = (
        <PageHeader
            icon={BarChart3}
            title="Dashboard"
            subtitle="Visão consolidada · receita, envios e cliques agregados"
        />
    );

    if (error && !data) {
        return (
            <PageShell hero={hero}>
                <ApiBanner error={error} onRetry={() => refetch()} />
            </PageShell>
        );
    }

    const kpis = data?.kpis;
    const points = data?.revenueTimeseries?.points ?? [];
    const mix = data?.mix ?? [];
    const topFlows = data?.topFlows ?? [];
    const topBroadcasts = data?.topBroadcasts ?? [];
    const topEsteiras = data?.topEsteiras ?? [];

    // Backend gap: failureRate vem `failures/sends * 100` (passa de 100%).
    // Correto = failures / (sends + failures). Recalculamos.
    const sendsN = cur(kpis?.sends);
    const failuresN = cur(kpis?.failures);
    const failureRate = correctFailureRate(failuresN, sendsN);

    // Map timeseries → chart format (label = dia do ISO)
    const daily = points.map((p) => {
        const ts = p.ts || "";
        return {
            day: ts.length >= 10 ? ts.slice(8, 10) : ts.slice(-2),
            flow: p.flow || 0,
            bc: p.broadcast || 0,
            est: p.esteira || 0,
            total: p.total || 0,
        };
    });

    const mixData = mix.map((m) => ({
        name:
            m.source === "flow"
                ? "Flow"
                : m.source === "broadcast"
                  ? "Broadcast"
                  : "Esteira",
        value: m.revenue || 0,
        sharePct: m.sharePct || 0,
        color: MIX_COLORS[m.source] || "#94a3b8",
    }));
    const mixTotal = mixData.reduce((a, b) => a + b.value, 0);

    return (
        <PageShell hero={hero}>
            {/* KPI strip */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                <KpiCard
                    icon={DollarSign}
                    iconColor="text-emerald-500"
                    label="Receita Total"
                    value={
                        loading ? (
                            <SkelText />
                        ) : (
                            "$" +
                            Number(cur(kpis?.revenue)).toLocaleString("pt-BR", {
                                maximumFractionDigits: 2,
                            })
                        )
                    }
                    sub="todas as origens"
                    delta={fmtDelta(kpis?.revenue)}
                    deltaDir={deltaDir(kpis?.revenue)}
                />
                <KpiCard
                    icon={Send}
                    iconColor="text-blue-500"
                    label="Envios"
                    value={loading ? <SkelText /> : fmt(Math.round(sendsN))}
                    sub={`${fmtPct(failureRate)} falhou`}
                    delta={fmtDelta(kpis?.sends)}
                    deltaDir={deltaDir(kpis?.sends)}
                />
                <KpiCard
                    icon={MousePointerClick}
                    iconColor="text-violet-500"
                    label={`Cliques · CTR ${fmtPct(cur(kpis?.ctr))}`}
                    value={
                        loading ? (
                            <SkelText />
                        ) : (
                            fmt(Math.round(cur(kpis?.clicks)))
                        )
                    }
                    sub="no período"
                    delta={fmtDelta(kpis?.ctr)}
                    deltaDir={deltaDir(kpis?.ctr)}
                    onClick={() => setView({ name: "cliques" })}
                />
                <KpiCard
                    icon={AlertCircle}
                    iconColor="text-rose-500"
                    label="Falhas"
                    value={loading ? <SkelText /> : fmt(Math.round(failuresN))}
                    sub={`${fmtPct(failureRate)} do total`}
                    delta={fmtDelta(kpis?.failures)}
                    deltaDir={deltaDir(kpis?.failures)}
                />
                <KpiCard
                    icon={Flame}
                    iconColor="text-amber-500"
                    label="Top flow · $"
                    value={
                        loading ? (
                            <SkelText />
                        ) : (
                            kpis?.topFlow?.name || "—"
                        )
                    }
                    sub={
                        kpis?.topFlow
                            ? `$${Number(
                                  kpis.topFlow.revenue
                              ).toLocaleString("pt-BR", {
                                  maximumFractionDigits: 2,
                              })}${
                                  kpis.topFlow.sharePct != null
                                      ? ` · ${kpis.topFlow.sharePct.toFixed(1)}%`
                                      : ""
                              }`
                            : ""
                    }
                    onClick={
                        kpis?.topFlow
                            ? () =>
                                  setView(
                                      { name: "flow", id: kpis.topFlow!.id },
                                      kpis.topFlow!.name
                                  )
                            : undefined
                    }
                />
            </div>

            {/* Revenue chart + mix */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <SectionCard
                    icon={TrendingUp}
                    title="Receita por dia"
                    subtitle={`${data?.revenueTimeseries?.granularity || "day"} · ${daily.length} pontos`}
                    className="lg:col-span-2"
                    right={
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                            <LegendDot color="#0f172a" label="Total" />
                            <LegendDot color="#3b82f6" label="Flow" />
                            <LegendDot color="#f59e0b" label="Broadcast" />
                            <LegendDot color="#8b5cf6" label="Esteira" />
                        </div>
                    }
                >
                    {loading ? (
                        <DashSkeleton h={288} />
                    ) : daily.length === 0 ? (
                        <div className="h-72 flex items-center justify-center text-sm text-gray-400">
                            Sem dados no período
                        </div>
                    ) : (
                        <div className="h-72">
                            <ResponsiveContainer>
                                <ComposedChart
                                    data={daily}
                                    margin={{
                                        top: 5,
                                        right: 10,
                                        bottom: 0,
                                        left: -10,
                                    }}
                                >
                                    <defs>
                                        <linearGradient
                                            id="gFlowH"
                                            x1="0"
                                            y1="0"
                                            x2="0"
                                            y2="1"
                                        >
                                            <stop
                                                offset="0%"
                                                stopColor="#3b82f6"
                                                stopOpacity={0.25}
                                            />
                                            <stop
                                                offset="100%"
                                                stopColor="#3b82f6"
                                                stopOpacity={0}
                                            />
                                        </linearGradient>
                                        <linearGradient
                                            id="gBcH"
                                            x1="0"
                                            y1="0"
                                            x2="0"
                                            y2="1"
                                        >
                                            <stop
                                                offset="0%"
                                                stopColor="#f59e0b"
                                                stopOpacity={0.18}
                                            />
                                            <stop
                                                offset="100%"
                                                stopColor="#f59e0b"
                                                stopOpacity={0}
                                            />
                                        </linearGradient>
                                        <linearGradient
                                            id="gEstH"
                                            x1="0"
                                            y1="0"
                                            x2="0"
                                            y2="1"
                                        >
                                            <stop
                                                offset="0%"
                                                stopColor="#8b5cf6"
                                                stopOpacity={0.18}
                                            />
                                            <stop
                                                offset="100%"
                                                stopColor="#8b5cf6"
                                                stopOpacity={0}
                                            />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke="#f1f5f9"
                                        vertical={false}
                                    />
                                    <XAxis
                                        dataKey="day"
                                        tick={{
                                            fontSize: 11,
                                            fill: "#64748b",
                                        }}
                                        tickLine={false}
                                        axisLine={{ stroke: "#e5e7eb" }}
                                    />
                                    <YAxis
                                        tick={{
                                            fontSize: 11,
                                            fill: "#64748b",
                                        }}
                                        tickLine={false}
                                        axisLine={false}
                                        tickFormatter={(v: number) =>
                                            "$" +
                                            (v >= 1000
                                                ? (v / 1000).toFixed(0) + "k"
                                                : v)
                                        }
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            borderRadius: 8,
                                            border: "1px solid #e5e7eb",
                                            fontSize: 12,
                                        }}
                                        formatter={(value, name) => [
                                            "$" +
                                                Number(value).toLocaleString(
                                                    "pt-BR",
                                                    {
                                                        maximumFractionDigits: 2,
                                                    }
                                                ),
                                            name === "flow"
                                                ? "Flow"
                                                : name === "bc"
                                                  ? "Broadcast"
                                                  : name === "est"
                                                    ? "Esteira"
                                                    : name === "total"
                                                      ? "Total"
                                                      : String(name),
                                        ]}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="flow"
                                        stroke="#3b82f6"
                                        strokeWidth={2}
                                        fill="url(#gFlowH)"
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="bc"
                                        stroke="#f59e0b"
                                        strokeWidth={2}
                                        fill="url(#gBcH)"
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="est"
                                        stroke="#8b5cf6"
                                        strokeWidth={2}
                                        fill="url(#gEstH)"
                                        strokeDasharray="4 4"
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="total"
                                        stroke="#0f172a"
                                        strokeWidth={2.2}
                                        dot={false}
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </SectionCard>

                <SectionCard icon={Layers} title="Mix de origem · $">
                    {loading ? (
                        <DashSkeleton h={140} />
                    ) : (
                        <>
                            <div className="flex items-center gap-4">
                                <div className="w-32 h-32 relative shrink-0">
                                    <ResponsiveContainer>
                                        <PieChart>
                                            <Pie
                                                data={mixData}
                                                dataKey="value"
                                                innerRadius={38}
                                                outerRadius={60}
                                                paddingAngle={2}
                                            >
                                                {mixData.map((d, i) => (
                                                    <Cell
                                                        key={i}
                                                        fill={d.color}
                                                    />
                                                ))}
                                            </Pie>
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                        <div className="text-[10px] text-gray-500 uppercase font-medium">
                                            Total
                                        </div>
                                        <div className="text-sm font-bold text-gray-900">
                                            ${(mixTotal / 1000).toFixed(1)}k
                                        </div>
                                    </div>
                                </div>
                                <div className="flex-1 space-y-2 min-w-0">
                                    {mixData.map((d) => (
                                        <div
                                            key={d.name}
                                            className="flex items-center justify-between text-sm"
                                        >
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span
                                                    className="w-2.5 h-2.5 rounded-sm"
                                                    style={{
                                                        background: d.color,
                                                    }}
                                                />
                                                <span className="text-gray-700 truncate">
                                                    {d.name}
                                                </span>
                                            </div>
                                            <div className="text-gray-900 font-semibold text-xs shrink-0">
                                                {d.sharePct.toFixed(1)}% · $
                                                {Number(
                                                    d.value
                                                ).toLocaleString("pt-BR", {
                                                    maximumFractionDigits: 0,
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs">
                                <span className="text-gray-500">
                                    RPM médio
                                </span>
                                <span className="text-gray-900 font-semibold">
                                    $
                                    {(
                                        typeof kpis?.rpm === "number"
                                            ? kpis.rpm
                                            : cur(kpis?.rpm)
                                    ).toFixed(2)}
                                </span>
                            </div>
                        </>
                    )}
                </SectionCard>
            </div>

            {/* Rankings clicáveis */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <SectionCard
                    icon={Flame}
                    title="Top Flows · Receita"
                    right={
                        <button
                            className="text-xs text-blue-600 font-medium flex items-center gap-1"
                            onClick={() => setView({ name: "flows" })}
                        >
                            ver todos
                            <ArrowRight className="w-3 h-3" />
                        </button>
                    }
                >
                    {loading ? (
                        <DashSkeleton h={180} />
                    ) : topFlows.length === 0 ? (
                        <EmptyMsg />
                    ) : (
                        <div className="space-y-2">
                            {topFlows.slice(0, 5).map((f, i) => {
                                const max = topFlows[0].revenue || 1;
                                return (
                                    <button
                                        key={f.id}
                                        onClick={() =>
                                            setView(
                                                { name: "flow", id: f.id },
                                                f.name
                                            )
                                        }
                                        className="w-full text-left hover:bg-gray-50 -mx-2 px-2 py-1.5 rounded-lg transition-colors group"
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="text-[10px] font-mono text-gray-400 shrink-0">
                                                    #{i + 1}
                                                </span>
                                                <span className="text-sm text-gray-800 font-medium truncate group-hover:text-blue-600">
                                                    {f.name}
                                                </span>
                                            </div>
                                            <span className="text-sm font-semibold text-gray-900 shrink-0">
                                                $
                                                {Number(
                                                    f.revenue
                                                ).toLocaleString("pt-BR", {
                                                    maximumFractionDigits: 2,
                                                })}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden grow">
                                                <div
                                                    className="h-full rounded-full"
                                                    style={{
                                                        width: `${(f.revenue / max) * 100}%`,
                                                        background:
                                                            i === 0
                                                                ? "#3b82f6"
                                                                : "#cbd5e1",
                                                    }}
                                                />
                                            </div>
                                            <span className="text-[10px] font-mono text-gray-400 shrink-0">
                                                {f.sends ? fmt(f.sends) : "—"}{" "}
                                                ·{" "}
                                                {f.ctr
                                                    ? f.ctr.toFixed(1) + "%"
                                                    : "—"}
                                            </span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </SectionCard>

                <SectionCard
                    icon={Send}
                    title="Top Broadcasts · Receita"
                    right={
                        <button
                            className="text-xs text-blue-600 font-medium flex items-center gap-1"
                            onClick={() => setView({ name: "broadcasts" })}
                        >
                            ver todos
                            <ArrowRight className="w-3 h-3" />
                        </button>
                    }
                >
                    {loading ? (
                        <DashSkeleton h={180} />
                    ) : topBroadcasts.length === 0 ? (
                        <EmptyMsg />
                    ) : (
                        <div className="space-y-2">
                            {topBroadcasts.slice(0, 5).map((b, i) => (
                                <button
                                    key={b.id}
                                    onClick={() =>
                                        setView(
                                            { name: "broadcast", id: b.id },
                                            b.name
                                        )
                                    }
                                    className="w-full text-left hover:bg-gray-50 -mx-2 px-2 py-1.5 rounded-lg transition-colors group"
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="text-[10px] font-mono text-gray-400 shrink-0">
                                                #{i + 1}
                                            </span>
                                            <div className="min-w-0">
                                                <p className="text-sm text-gray-800 font-medium truncate group-hover:text-blue-600">
                                                    {b.name}
                                                </p>
                                                <p className="text-[10px] text-gray-400">
                                                    {b.pageName || "—"}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <p className="text-sm font-semibold text-gray-900">
                                                $
                                                {Number(
                                                    b.revenue
                                                ).toLocaleString("pt-BR", {
                                                    maximumFractionDigits: 2,
                                                })}
                                            </p>
                                            <p className="text-[10px] font-mono text-gray-400">
                                                {b.ctr
                                                    ? b.ctr.toFixed(1) + "%"
                                                    : "—"}
                                            </p>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </SectionCard>

                <SectionCard
                    icon={Radio}
                    title="Top Esteiras · Receita"
                    right={
                        <button
                            className="text-xs text-blue-600 font-medium flex items-center gap-1"
                            onClick={() => setView({ name: "esteira" })}
                        >
                            ver todas
                            <ArrowRight className="w-3 h-3" />
                        </button>
                    }
                >
                    {loading ? (
                        <DashSkeleton h={180} />
                    ) : topEsteiras.length === 0 ? (
                        <EmptyMsg />
                    ) : (
                        <div className="space-y-2">
                            {topEsteiras.slice(0, 5).map((e, i) => {
                                const max = topEsteiras[0].revenue || 1;
                                return (
                                    <button
                                        key={e.id}
                                        onClick={() =>
                                            setView(
                                                {
                                                    name: "esteira_group",
                                                    id: e.id,
                                                },
                                                e.name
                                            )
                                        }
                                        className="w-full text-left hover:bg-gray-50 -mx-2 px-2 py-1.5 rounded-lg transition-colors group"
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="text-[10px] font-mono text-gray-400 shrink-0">
                                                    #{i + 1}
                                                </span>
                                                {e.flag && (
                                                    <span>{e.flag}</span>
                                                )}
                                                <span className="text-sm text-gray-800 font-medium truncate group-hover:text-blue-600">
                                                    {e.name}
                                                </span>
                                            </div>
                                            <span className="text-sm font-semibold text-gray-900 shrink-0">
                                                $
                                                {Number(
                                                    e.revenue
                                                ).toLocaleString("pt-BR", {
                                                    maximumFractionDigits: 2,
                                                })}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden grow">
                                                <div
                                                    className="h-full rounded-full"
                                                    style={{
                                                        width: `${(e.revenue / max) * 100}%`,
                                                        background:
                                                            i === 0
                                                                ? "#8b5cf6"
                                                                : "#cbd5e1",
                                                    }}
                                                />
                                            </div>
                                            <span className="text-[10px] font-mono text-gray-400 shrink-0">
                                                {e.broadcasts
                                                    ? fmt(e.broadcasts) + " bc"
                                                    : "—"}
                                            </span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </SectionCard>
            </div>

            {/* Drilldown shortcuts */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <DrilldownTile
                    icon={MousePointerClick}
                    color="violet"
                    label="Análise de cliques"
                    desc="Torre/barras 3D por step · hora pico"
                    onClick={() => setView({ name: "cliques" })}
                />
                <DrilldownTile
                    icon={Globe2}
                    color="blue"
                    label="Receita por país"
                    desc="Mapa global + ranking · RPM por região"
                    onClick={() => setView({ name: "paises" })}
                />
                <DrilldownTile
                    icon={Radio}
                    color="amber"
                    label="Heatmap esteira"
                    desc="Hora × dia × receita esteira"
                    onClick={() => setView({ name: "esteira" })}
                />
                <DrilldownTile
                    icon={Layers}
                    color="emerald"
                    label="Comparar origens"
                    desc="Flow × Broadcast × Esteira"
                    onClick={() => setView({ name: "esteira" })}
                />
            </div>
        </PageShell>
    );
}

function EmptyMsg() {
    return (
        <div className="text-center py-8">
            <AlertCircle className="w-6 h-6 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Sem dados no período</p>
        </div>
    );
}

interface DrilldownTileProps {
    icon: LucideIcon;
    color: "blue" | "amber" | "violet" | "emerald";
    label: string;
    desc: string;
    onClick: () => void;
}

const TILE_COLORS = {
    blue: { bg: "bg-blue-50", icon: "text-blue-500" },
    amber: { bg: "bg-amber-50", icon: "text-amber-500" },
    violet: { bg: "bg-violet-50", icon: "text-violet-500" },
    emerald: { bg: "bg-emerald-50", icon: "text-emerald-500" },
};

function DrilldownTile({
    icon: Icon,
    color,
    label,
    desc,
    onClick,
}: DrilldownTileProps) {
    const c = TILE_COLORS[color];
    return (
        <button
            onClick={onClick}
            className="bg-white rounded-xl border border-gray-100 p-4 text-left hover:shadow-md hover:border-blue-200 transition-all group"
        >
            <div className="flex items-start gap-3">
                <div className={cn("p-2 rounded-lg shrink-0", c.bg)}>
                    <Icon className={cn("w-5 h-5", c.icon)} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                        <span className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 truncate">
                            {label}
                        </span>
                        <ArrowUpRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-blue-500 shrink-0" />
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed">
                        {desc}
                    </p>
                </div>
            </div>
        </button>
    );
}
